import { useState } from "react";
import {
  CloudOff,
  Cloud,
  RefreshCw,
  Trash2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useOfflineQueue, useOnlineStatus } from "@/hooks";
import { syncNow } from "@/lib/offline/sync";
import { clearSynced, markStatus, removeFromQueue } from "@/lib/offline/queue";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { PageHeader, EmptyState } from "@/components/common";
import type { CreateOrderRequest } from "@/types";

export function PendingOrdersPage() {
  const { orders } = useOfflineQueue();
  const online = useOnlineStatus();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSyncNow = async () => {
    setIsSyncing(true);
    try {
      await syncNow();
      toast.success("Sync complete");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClearSynced = async () => {
    const removed = await clearSynced();
    if (removed === 0) {
      toast.info("Nothing to clear");
    } else {
      toast.success(
        `Removed ${removed} synced ${removed === 1 ? "order" : "orders"}`,
      );
    }
  };

  const handleRetry = async (id: string) => {
    await markStatus(id, "pending", { error: null });
    void syncNow();
    toast.success("Queued for retry");
  };

  const handleDiscard = async (id: string) => {
    await removeFromQueue(id);
    toast.success("Discarded");
  };

  const pending = orders.filter(
    (o) => o.status === "pending" || o.status === "syncing",
  );
  const failed = orders.filter((o) => o.status === "failed");
  const synced = orders.filter((o) => o.status === "synced");

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Pending Orders"
        description="Orders queued while offline. They sync automatically when the connection is back."
        actions={
          <>
            <Badge
              variant={online ? "default" : "destructive"}
              className="gap-1.5"
            >
              {online ? (
                <Cloud className="h-3.5 w-3.5" />
              ) : (
                <CloudOff className="h-3.5 w-3.5" />
              )}
              {online ? "Online" : "Offline"}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncNow}
              disabled={!online || isSyncing || pending.length === 0}
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${isSyncing ? "animate-spin" : ""}`}
              />
              Sync now
            </Button>
            {synced.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClearSynced}>
                Clear synced
              </Button>
            )}
          </>
        }
      />
      {orders.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="No queued orders"
          description="Every order has been synced to the server."
        />
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <Section title="Queued" count={pending.length}>
              {pending.map((order) => (
                <QueuedOrderCard
                  key={order.id}
                  order={order}
                  variant="pending"
                  onDiscard={() => handleDiscard(order.id)}
                />
              ))}
            </Section>
          )}

          {failed.length > 0 && (
            <Section title="Failed" count={failed.length} accent="destructive">
              {failed.map((order) => (
                <QueuedOrderCard
                  key={order.id}
                  order={order}
                  variant="failed"
                  onRetry={() => handleRetry(order.id)}
                  onDiscard={() => handleDiscard(order.id)}
                />
              ))}
            </Section>
          )}

          {synced.length > 0 && (
            <Section
              title="Recently synced"
              count={synced.length}
              accent="muted"
            >
              {synced.map((order) => (
                <QueuedOrderCard
                  key={order.id}
                  order={order}
                  variant="synced"
                  onDiscard={() => handleDiscard(order.id)}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  count: number;
  accent?: "default" | "destructive" | "muted";
  children: React.ReactNode;
}

function Section({ title, count, accent = "default", children }: SectionProps) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
        <Badge variant={accent === "destructive" ? "destructive" : "secondary"}>
          {count}
        </Badge>
      </div>
      <div className="grid gap-3">{children}</div>
    </div>
  );
}

interface QueuedOrderCardProps {
  order: {
    id: string;
    idempotencyKey: string;
    payload: unknown;
    createdAt: number;
    attemptCount: number;
    status: string;
    error: string | null;
    serverOrderId: string | null;
  };
  variant: "pending" | "failed" | "synced";
  onRetry?: () => void;
  onDiscard?: () => void;
}

function QueuedOrderCard({
  order,
  variant,
  onRetry,
  onDiscard,
}: QueuedOrderCardProps) {
  const payload = order.payload as CreateOrderRequest;
  const itemCount =
    payload?.items?.reduce((sum, i) => sum + i.quantity, 0) ?? 0;
  const createdAt = new Date(order.createdAt).toLocaleString();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="font-mono text-xs text-muted-foreground">
            {order.idempotencyKey.slice(0, 8)}…
          </span>
          <span className="text-sm text-muted-foreground">{createdAt}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm">
          {itemCount} {itemCount === 1 ? "item" : "items"} ·{" "}
          {payload?.orderType ?? "—"}
          {payload?.payment ? ` · ${payload.payment.method}` : " · pay later"}
        </div>
        {variant === "failed" && order.error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              <p className="font-medium text-destructive">
                Server rejected this order
              </p>
              <p className="text-muted-foreground">{order.error}</p>
            </div>
          </div>
        )}

        {order.attemptCount > 0 && variant === "pending" && (
          <p className="text-xs text-muted-foreground">
            Attempt {order.attemptCount} · waiting for next retry
          </p>
        )}

        {variant === "synced" && order.serverOrderId && (
          <p className="text-xs text-green-700">
            Synced as order {order.serverOrderId.slice(0, 8)}
          </p>
        )}

        <div className="flex gap-2">
          {variant === "failed" && onRetry && (
            <Button size="sm" onClick={onRetry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          )}
          {onDiscard && (
            <Button size="sm" variant="outline" onClick={onDiscard}>
              <Trash2 className="mr-2 h-4 w-4" />
              {variant === "synced" ? "Remove" : "Discard"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
